import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { User, Shield, CheckCircle, Key, LogOut, Loader2 } from 'lucide-react'
import { MfaSetup } from '@/components/auth/MfaSetup'
import { Button } from '@/components/ui/button'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/useToast'
import type { Factor } from '@supabase/supabase-js'

export function SettingsPage() {
  const { adminUser, mfaFactors } = useAuth()
  const { toast } = useToast()
  const [showMfaSetup, setShowMfaSetup] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [signOutLoading, setSignOutLoading] = useState(false)

  const verifiedFactors = mfaFactors.filter((f: Factor) => f.status === 'verified')
  const hasMfaEnabled = verifiedFactors.length > 0

  const formatRole = (role: string) => {
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }

  if (showMfaSetup) {
    return (
      <div className="max-w-md mx-auto">
        <MfaSetup onComplete={() => setShowMfaSetup(false)} onCancel={() => setShowMfaSetup(false)} />
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold">Settings</h2>
        <p className="text-sm sm:text-base text-muted-foreground">Manage your account and security settings</p>
      </div>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <User className="h-5 w-5" />
            Profile
          </CardTitle>
          <CardDescription className="text-sm">Your account information</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-4">
          <div className="space-y-1">
            <p className="text-xs sm:text-sm text-muted-foreground">Email</p>
            <p className="font-medium text-sm sm:text-base break-all">{adminUser?.email || 'Not set'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs sm:text-sm text-muted-foreground">Role</p>
            <p className="font-medium text-sm sm:text-base">{adminUser?.role ? formatRole(adminUser.role) : 'Unknown'}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Shield className="h-5 w-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription className="text-sm">
            Two-factor authentication is required for all accounts
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm sm:text-base">Authenticator App</p>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {hasMfaEnabled ? 'Enabled' : 'Setting up...'}
                </p>
              </div>
            </div>
            <span className="text-xs text-muted-foreground">Required</span>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Key className="h-5 w-5" />
            Change Password
          </CardTitle>
          <CardDescription className="text-sm">Update your account password</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              if (newPassword !== confirmPassword) {
                toast({ title: 'Error', description: 'Passwords do not match.', variant: 'destructive' })
                return
              }
              if (newPassword.length < 8) {
                toast({ title: 'Error', description: 'Password must be at least 8 characters.', variant: 'destructive' })
                return
              }
              setPasswordLoading(true)
              try {
                const { error } = await supabase.auth.updateUser({ password: newPassword })
                if (error) throw error
                toast({ title: 'Password updated', description: 'Your password has been changed.' })
                setNewPassword('')
                setConfirmPassword('')
              } catch (err) {
                toast({
                  title: 'Error',
                  description: err instanceof Error ? err.message : 'Failed to update password.',
                  variant: 'destructive',
                })
              } finally {
                setPasswordLoading(false)
              }
            }}
            className="space-y-4 max-w-sm"
          >
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                required
                minLength={8}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                required
                minLength={8}
              />
            </div>
            <Button type="submit" disabled={passwordLoading} className="bg-dextoro text-black hover:bg-dextoro/90">
              {passwordLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Update Password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Sessions */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <LogOut className="h-5 w-5" />
            Sessions
          </CardTitle>
          <CardDescription className="text-sm">Manage your active sessions</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <div className="flex items-center justify-between p-3 sm:p-4 rounded-lg bg-muted/50">
            <div>
              <p className="font-medium text-sm">Current Session</p>
              <p className="text-xs text-muted-foreground">{adminUser?.email}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={signOutLoading}
              onClick={async () => {
                setSignOutLoading(true)
                try {
                  await supabase.auth.signOut({ scope: 'others' })
                  toast({ title: 'Done', description: 'All other sessions have been signed out.' })
                } catch {
                  toast({ title: 'Error', description: 'Failed to sign out other sessions.', variant: 'destructive' })
                } finally {
                  setSignOutLoading(false)
                }
              }}
            >
              {signOutLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Sign out other sessions
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
